import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { isAfter, isToday, parseISO } from "date-fns";
import {
  EAcaoLogUser,
  EApplication,
  EGrupoAcaoLogUser,
  ELogUserStatus,
  EMensagemCategoriaTipo,
  EUserType,
  IAlunosChamada,
  IDiaAula,
  IFalta,
  IFiltroSerie,
  IHorarioAulaDiaAula,
  IMensagem,
  IMensagemCategoria,
  IPeriodoEtapa,
  ITurmaObs,
  IUser,
  Log,
  utils,
  yupDiaAula,
  yupMensagem,
  yupMensagemAtribuicao,
  yupTurmaObs,
} from "@deltasge/marauders-map";
import { LinearProgress } from "@mui/material";
import { AccountCog as SettingIcon } from "mdi-material-ui";

import { api, Environment, history } from "configs";
import { snack } from "components/GlobalSnackbar";
import { Main } from "pages/_layouts/Main";
import { getError } from "utils";

import { DadosAula } from "./components/DadosAula";
import { useAppSelector, useAppDispatch } from "store";
import { request as getCategorias } from "store/modules/categorias";
import { request as getModelos } from "store/modules/relatorios/diarios/modelos";
import { request as getPapeis } from "store/modules/relatorios/configuracoes/papeis";
import { request as getSistemaAvaliacao } from "store/modules/sistema-avaliacao";
import { useSessionStorage } from "react-use";
import { utilMensagem } from "pages/Mensagens/utilMensagem";
import { gravaLogUser } from "configs/log";
import { Alunos } from "./components/Alunos";
import { Backdrop } from "components/Backdrop";
import { Subtitle } from "./components/Subtitle";
import { useDialog } from "hooks/useDialog";
import { AlunosRecuperacao } from "./components/AlunosRecuperacao";

const getMensagemDefault = ({
  aula,
  idMensagemCategoria,
  mensagemCategoria,
  usuario,
}: {
  aula: IDiaAula;
  idMensagemCategoria: number;
  mensagemCategoria: IMensagemCategoria;
  usuario: IUser | null;
}) => ({
  ...yupMensagem.getDefault(),
  texto: "",
  rascunho: false,
  idMensagemCategoria: idMensagemCategoria,
  idUsuario:
    usuario?.tipoUsuario == EUserType.Professor ? null : usuario?.id ?? null,
  idProfessor:
    usuario?.tipoUsuario == EUserType.Professor ? usuario?.id ?? null : null,
  mensagemCategoria,
  mensagemAtribuicoes: [
    {
      ...yupMensagemAtribuicao.getDefault(),
      idCurso: aula.idCurso,
      idSerie: aula.idSerie,
      idTurma: aula.idTurma,
      idMateria: aula.idMateria,
      anoLetivo: aula.anoLetivo,
      aluno: true,
      responsavel: true,
      excluido: false,
      exportado: false,
    },
  ],
});
export interface IChamada extends IAlunosChamada {
  selected: boolean;
}

export const GerenciarAula: FC = () => {
  const { idDiaAula } = useParams<{
    idDiaAula: string;
  }>();

  const {
    escola,
    usuario: { usuario },
    mensagemCategoria: { data: mensagemCategorias },
    modelosDiarios: { data: modelosDiarios },
    tamanhoPapeis: { data: tamanhoPapeis },
    configuracoes: { etapas: todasEtapas },
    treeView: { cursos },
    sistemaAvaliacao: { data: sistemaAvaliacao },
  } = useAppSelector((state) => state);

  const [aula, setAula] = useState<IDiaAula>();
  const [turmaObs, setTurmaObs] = useState<ITurmaObs>();
  const [alunos, setAlunos] = useState<IChamada[]>([]);
  const [mensagemCategoria, setMensagemCategoria] =
    useState<IMensagemCategoria>();
  const [precisaNotificarTarefa, setPrecisaNotificarTarefa] = useState(false);
  const [textoAulaInicial, setTextoAulaInicial] = useState<string | null>("");
  const [textoTarefaInicial, setTextoTarefaInicial] = useState<string | null>(
    ""
  );
  const [textoObsInicial, setTextoObsInicial] = useState<string | null>("");
  const [loading, setLoading] = useState(false);
  const [msgErrorAttr, setMsgErrorAttr] = useState<string>();
  const [showTooltipTarefa, setShowTooltipTarefa] = useState(false);
  const [showTooltipAula, setShowTooltipAula] = useState(false);
  const [showTooltipObs, setShowTooltipObs] = useState(false);
  const [modificou, setModificou] = useState(false);
  const [horarios, setHorarios] = useState<IHorarioAulaDiaAula[]>();
  const [gerandoAula, setGerandoAula] = useState(false);
  const [carregaAlunos, setCarregaAlunos] = useState(false);

  const dispatch = useAppDispatch();

  const [conteudoCopiado, setConteudoCopiado] =
    useSessionStorage<string>("conteudo-copiado");

  let dataAula: Date | undefined;
  if (aula?.data) {
    dataAula = parseISO(aula.data);
  }
  const curso = utils.getCursoTreeView({
    cursos,
    type: usuario?.tipoUsuario,
    idCurso: aula?.idCurso?.toString() ?? null,
  });
  let etapas: IPeriodoEtapa[] | undefined = undefined;
  let serie: IFiltroSerie | undefined;
  if (curso) {
    etapas = todasEtapas.filter((etapa) => {
      return etapa.idsCursos?.includes(curso?.id ?? 0);
    });
    serie = utils.getSerieTreeView({
      cursos,
      idCurso: aula?.idCurso?.toString() ?? null,
      idSerie: aula?.idSerie?.toString() ?? null,
      type: usuario?.tipoUsuario,
    });
  }

  let isRecuperacao = false;
  let recEtapa: IPeriodoEtapa | undefined;
  if (dataAula && etapas) {
    const rec = utils.isPeriodoRecuperacao({
      etapas,
      day: dataAula,
    });
    isRecuperacao = rec.isRecuperacao;
    recEtapa = rec.etapa;
  }

  const gerarFalta = async (aluno: IChamada) => {
    try {
      setAlunos((alunos) =>
        alunos.map((m) => ({
          ...m,
          selected: m.idMatricula == aluno.idMatricula ? false : m.selected,
        }))
      );

      const { data: falta } = await api.post<IFalta | undefined>("falta", {
        anoLetivo: escola.anoSite,
        idDiaAula,
        idMatricula: aluno.idMatricula,
        id: null,
      });
      if (!falta) {
        throw new Error("Erro ao gerar a falta do aluno");
      }
      gravaLogUser({
        logKey: {
          ...Log.Alunos.Incluir,
          acao: EAcaoLogUser.Inclusao,
          grupoAcao: EGrupoAcaoLogUser.Aula,
        },
        logKeys: {
          idMatricula: falta.idMatricula,
          idAluno: aluno.alunoUid,
          idMateria: Number(aula?.idMateria),
          idProfessor: Number(aula?.idProfessor),
        },
        status: ELogUserStatus.Aguardando,
        message: `incluiu falta id ${falta.id} aluno ${aluno.alunoNome}`,
        dataAfter: { ...falta },
      });
      setAlunos((alunos) =>
        alunos.map((m) => ({
          ...m,
          idFalta: m.idMatricula == aluno.idMatricula ? falta.id : m.idFalta,
        }))
      );
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const apagarFalta = async (aluno: IChamada) => {
    try {
      if (!aluno.idFalta) return;
      setAlunos((alunos) =>
        alunos.map((m) => ({
          ...m,
          selected: m.idMatricula == aluno.idMatricula ? true : m.selected,
        }))
      );

      await api.delete(`falta/${aluno.idFalta}`);
      gravaLogUser({
        logKey: {
          ...Log.Alunos.Excluir,
          acao: EAcaoLogUser.Exclusao,
          grupoAcao: EGrupoAcaoLogUser.Aula,
        },
        logKeys: {
          idMatricula: Number(aluno.idMatricula),
          idAluno: Number(aluno.alunoUid),
          idMateria: Number(aula?.idMateria),
          idProfessor: Number(aula?.idProfessor),
        },
        status: ELogUserStatus.Aguardando,
        message: `excluiu falta do aluno ${aluno.alunoNome} id: ${aluno.alunoUid}`,
        dataBefore: { ...aluno },
      });
      setAlunos((alunos) =>
        alunos.map((m) => ({
          ...m,
          idFalta: m.idMatricula == aluno.idMatricula ? null : m.idFalta,
        }))
      );
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const apagarTodasFaltas = async () => {
    try {
      const faltasId = (alunos ?? [])
        .filter((f) => f.idFalta)
        .map((m) => m.idFalta) as number[];
      if (faltasId?.length > 0) {
        setAlunos((alunos) =>
          alunos.map((m) => ({
            ...m,
            idFalta: null,
            selected: true,
          }))
        );

        await api.delete(`falta/${faltasId?.join(",")}`);

        gravaLogUser({
          logKey: {
            ...Log.Alunos.Excluir,
            acao: EAcaoLogUser.Exclusao,
            grupoAcao: EGrupoAcaoLogUser.Aula,
          },
          logKeys: {
            idProfessor: Number(aula?.idProfessor),
            idMateria: Number(aula?.idMateria),
            idCurso: Number(aula?.idCurso),
            idSerie: Number(aula?.idSerie),
            idTurma: Number(aula?.idTurma),
          },
          status: ELogUserStatus.Aguardando,
          message: `excluiu ${faltasId.length} faltas na aula ${
            aula?.aula
          }, ids: ${faltasId.join(",")}`,
          dataBefore: { ...aula },
        });
      }
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const gravarAula = async (aula: IDiaAula) => {
    try {
      let idTarefa: number | undefined = undefined;
      if (
        aula.diaSemAula &&
        aula.idMensagem &&
        mensagemCategoria &&
        mensagemCategoria.id
      ) {
        idTarefa = aula.idMensagem;
        aula.mensagem = getMensagemDefault({
          aula,
          usuario,
          mensagemCategoria,
          idMensagemCategoria: mensagemCategoria.id,
        });
        aula.idMensagem = null;
      }

      setAula(() => ({ ...aula }));

      if (aula.diaSemAula) {
        await apagarTodasFaltas();
      }

      await api.put(`diaaula/${aula.id}`, {
        aula: aula.aula,
        data: aula.data,
        descricao: aula.descricao,
        diaSemAula: aula.diaSemAula,
        isRecuperacao: aula.isRecuperacao,
        excluido: aula.excluido,
        exportado: aula.exportado,
        id: aula.id,
        idCurso: aula.idCurso,
        idMateria: aula.idMateria,
        idMensagem: aula.idMensagem,
        idProfessor:
          usuario?.tipoUsuario == EUserType.Professor ? usuario?.id : null,
        idSerie: aula.idSerie,
        idTurma: aula.idTurma,
        idUsuario:
          usuario?.tipoUsuario == EUserType.UsuarioSistema ? usuario?.id : null,
        inutilizaDia: aula.inutilizaDia,
        tarefa: aula.tarefa,
        anoLetivo: escola.anoSite,
      });
      setTextoAulaInicial(aula.descricao);
      setModificou(false);

      gravaLogUser({
        logKey: {
          ...Log.Alunos.Alterar,
          acao: EAcaoLogUser.Alteracao,
          grupoAcao: EGrupoAcaoLogUser.Aula,
        },
        logKeys: {
          idProfessor: Number(aula.idProfessor),
          idMateria: Number(aula.idMateria),
          idCurso: Number(aula.idCurso),
          idSerie: Number(aula.idSerie),
          idTurma: Number(aula.idTurma),
        },
        status: ELogUserStatus.Aguardando,
        message: `alterou aula ${aula.aula} id: ${aula.id} matéria ${aula.idMateria}`,
        dataBefore: { ...aula },
        dataAfter: { ...aula },
      });
      if (idTarefa) {
        await api.delete(`educador/mensagem/${idTarefa}`);

        gravaLogUser({
          logKey: {
            ...Log.Alunos.Excluir,
            acao: EAcaoLogUser.Exclusao,
            grupoAcao: EGrupoAcaoLogUser.Aula,
          },
          logKeys: {
            idProfessor: Number(aula.idProfessor),
            idMateria: Number(aula.idMateria),
            idCurso: Number(aula.idCurso),
            idSerie: Number(aula.idSerie),
            idTurma: Number(aula.idTurma),
          },
          status: ELogUserStatus.Aguardando,
          message: `excluiu tarefa id: ${idTarefa}`,
          dataBefore: { ...aula },
        });
      }
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const removerTarefa = async () => {
    if (!aula) return;
    if (!mensagemCategoria || !mensagemCategoria.id) return;

    const idTarefa = aula.idMensagem;
    aula.mensagem = getMensagemDefault({
      aula,
      usuario,
      mensagemCategoria,
      idMensagemCategoria: mensagemCategoria.id,
    });
    await gravarAula({ ...aula, idMensagem: null });
    await api.delete(`educador/mensagem/${idTarefa}`);

    gravaLogUser({
      logKey: {
        ...Log.Alunos.Excluir,
        acao: EAcaoLogUser.Exclusao,
        grupoAcao: EGrupoAcaoLogUser.Aula,
      },
      logKeys: {
        idProfessor: Number(aula.idProfessor),
        idMateria: Number(aula.idMateria),
        idCurso: Number(aula.idCurso),
        idSerie: Number(aula.idSerie),
        idTurma: Number(aula.idTurma),
      },
      status: ELogUserStatus.Aguardando,
      message: `excluiu tarefa id: ${idTarefa}`,
      dataBefore: { ...aula },
    });

    snack.success("Tarefa removida com sucesso!");
  };

  const removerObservacao = async () => {
    if (!turmaObs || !turmaObs.id) {
      return;
    }

    await api.delete(`observacao-turma/${turmaObs.id}`);

    setTurmaObs({
      ...yupTurmaObs.getDefault(),
      anoLetivo: turmaObs.anoLetivo,
      data: turmaObs.data,
      idCurso: turmaObs.idCurso,
      idSerie: turmaObs.idSerie,
      idTurma: turmaObs.idTurma,
      idMateria: turmaObs.idMateria,
      idProfessor:
        usuario?.tipoUsuario == EUserType.Professor ? usuario.id || null : null,
      observacao: "",
    });
    setTextoObsInicial("");

    gravaLogUser({
      logKey: {
        ...Log.Alunos.Excluir,
        acao: EAcaoLogUser.Exclusao,
        grupoAcao: EGrupoAcaoLogUser.Aula,
      },
      logKeys: {
        idProfessor: Number(turmaObs.idProfessor),
        idMateria: turmaObs.idMateria,
        idCurso: turmaObs.idCurso,
        idSerie: turmaObs.idSerie,
        idTurma: turmaObs.idTurma,
      },
      status: ELogUserStatus.Aguardando,
      message: `excluiu observação id: ${turmaObs.id}`,
      dataBefore: { ...turmaObs },
    });

    snack.success("Observação removida com sucesso!");
  };

  const gravarTarefa = async (mensagem: IMensagem) => {
    try {
      if (!aula) return;

      setAula(() => ({ ...aula, mensagem }));

      if (mensagem && mensagem.texto == "" && !aula.idMensagem) {
        return;
      }
      if (mensagem && mensagem.texto == "" && aula.idMensagem) {
        await removerTarefa();
        return;
      }

      const att = mensagem?.mensagemAtribuicoes?.at(0);

      if (att) {
        if (!att.aluno && !att.responsavel && !att.loginExtra) {
          setMsgErrorAttr("Você deve escolher quem vai receber a tarefa");
          return;
        } else {
          setMsgErrorAttr(undefined);
        }
      }

      if (!mensagem.id) {
        const { data } = await api.post<IMensagem>(
          "educador/mensagem",
          mensagem
        );
        if (data) aula.mensagem = { ...mensagem, ...data };
        await gravarAula({ ...aula, idMensagem: data.id ?? null });
      } else {
        const { id, ...rest } = mensagem;
        const { data } = await api.put<IMensagem>(
          `educador/mensagem/${id}`,
          rest
        );
        if (data) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { mensagemArquivos, ...rest } = data;
          mensagem = {
            ...mensagem,
            ...rest,
          };
        }
        gravaLogUser({
          logKey: {
            ...Log.Alunos.Alterar,
            acao: EAcaoLogUser.Alteracao,
            grupoAcao: EGrupoAcaoLogUser.Aula,
          },
          logKeys: {
            idProfessor: Number(data.idProfessor),
            idMateria: Number(aula.idMateria),
            idCurso: Number(aula.idCurso),
            idSerie: Number(aula.idSerie),
            idTurma: Number(aula.idTurma),
          },
          status: ELogUserStatus.Aguardando,
          message: `alterou tarefa ${data.titulo} id: ${data.id}`,
          dataBefore: { ...data },
          dataAfter: { ...rest },
        });
        setAula(() => ({ ...aula, mensagem }));
      }
      setTextoTarefaInicial(mensagem.texto);
      setPrecisaNotificarTarefa(
        isAfter(parseISO(aula.data), Date.now()) || isToday(parseISO(aula.data))
      );
      setModificou(false);
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const gravarObservacao = async (turmaObs: ITurmaObs) => {
    try {
      if (!turmaObs.observacao.trim() && !turmaObs.id) {
        return;
      }
      if (!turmaObs.observacao.trim() && turmaObs.id) {
        await removerObservacao();
        return;
      }

      const { id, ...payload } = turmaObs;

      if (!id) {
        const { data } = await api.post<ITurmaObs>("observacao-turma", payload);
        if (data) turmaObs = { ...data };
      } else {
        const { data } = await api.put<ITurmaObs>(
          `observacao-turma/${id}`,
          payload
        );
        if (data) turmaObs = { ...data };
      }

      gravaLogUser({
        logKey: {
          ...Log.Alunos.Alterar,
          acao: EAcaoLogUser.Alteracao,
          grupoAcao: EGrupoAcaoLogUser.Aula,
        },
        logKeys: {
          idProfessor: Number(turmaObs.idProfessor),
          idMateria: Number(aula?.idMateria),
          idCurso: Number(aula?.idCurso),
          idSerie: Number(aula?.idSerie),
          idTurma: Number(aula?.idTurma),
        },
        status: ELogUserStatus.Aguardando,
        message: `alterou observação da turma ${turmaObs.observacao} id: ${turmaObs.id}`,
        dataBefore: { ...turmaObs },
        dataAfter: { ...turmaObs },
      });

      setTextoObsInicial(turmaObs.observacao);
      setTurmaObs({ ...turmaObs });
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const getAlunos = async ({
    emRecuperacao,
    etapaRecuperacao,
  }:
    | {
        etapaRecuperacao?: number;
        emRecuperacao?: boolean;
      }
    | undefined = {}) => {
    try {
      setLoading(true);
      const { data: alunos } = await api.get<IAlunosChamada[] | undefined>(
        `/educador/chamada/${idDiaAula}`,
        {
          params: {
            etapaRecuperacao,
            emRecuperacao,
          },
          cache: { ignoreCache: true },
        }
      );
      if (alunos) {
        setAlunos([...alunos.map((m) => ({ ...m, selected: !m.idFalta }))]);
      }
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setLoading(false);
    }
  };

  const notificaTarefa = () => {
    if (usuario && usuario.escolaId && usuario.token) {
      const data: Record<string, string> = {
        token: usuario.token,
      };
      const params = new URLSearchParams(data).toString();
      navigator.sendBeacon(
        `${Environment.API_URL}/api/${usuario.escolaId}/educador/mensagem/notifica-tarefa-dia-aula/${idDiaAula}/?${params}`
      );
    }
    setPrecisaNotificarTarefa(false);
  };

  const alertUser = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    return (event.returnValue = "Deseja fechar o gerenciamento da aula?");
  };

  const handleChangeAula = async (horario: IHorarioAulaDiaAula) => {
    try {
      setGerandoAula(true);
      if (!aula) {
        throw new Error("Você precisa da aula para mudar de aula");
      }
      if (!horario.idDiaAula) {
        const { data } = await api.post<IDiaAula | undefined | null>(
          "diaaula",
          {
            anoLetivo: aula.anoLetivo,
            data: aula.data,
            aula: horario.aula,
            idCurso: aula.idCurso,
            idSerie: aula.idSerie,
            idTurma: aula.idTurma,
            idMateria: aula.idMateria,
            diaSemAula: false,
            inutilizaDia: false,
            idProfessor:
              usuario?.tipoUsuario === EUserType.Professor ? usuario?.id : null,
            idUsuario:
              usuario?.tipoUsuario === EUserType.UsuarioSistema
                ? usuario?.id
                : null,
            descricao: "",
            idMensagem: null,
            tarefa: "",
            id: null,
            naGrade: true,
            hora: horario.horaIni,
            duracao: horario.duracao,
            origem: EApplication.Educador,
          }
        );
        if (!data || !data.id) {
          throw new Error("Erro ao criar a aula");
        }
        horario.idDiaAula = data.id;
      }
      history.push(`/gerenciar-aula/${horario.idDiaAula}`);
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setGerandoAula(false);
    }
  };

  const {
    RenderDialog: DialogAlunosRecuperacao,
    show: showDialogAlunosRecuperacao,
    hide: hideDialogAlunosRecuperacao,
  } = useDialog({
    title: "Gerenciar alunos em recuperação",
    options: {
      maxWidth: "sm",
    },
  });

  useEffect(() => {
    if (serie?.idSistemaAvaliacao) {
      dispatch(getSistemaAvaliacao(serie.idSistemaAvaliacao));
    }
  }, [serie?.idSistemaAvaliacao, dispatch]);

  useEffect(() => {
    if (mensagemCategorias.length == 0) {
      dispatch(getCategorias());
    }
    (async () => {
      try {
        setLoading(true);

        if (modelosDiarios.length == 0 || tamanhoPapeis.length == 0) {
          dispatch(getModelos());
          dispatch(getPapeis());
        }

        const { data: aula } = await api.get<IDiaAula | undefined>(
          `/diaaula/${idDiaAula}`,
          {
            cache: { ignoreCache: true },
            params: {
              select: [
                ...Object.keys(yupDiaAula.fields),
                "anoLetivo",
                "curso.grau",
                "turma",
                "materia.nome",
                "materia.codigo",
                "usuario.nome",
                "professor.nome",
                ...utilMensagem.camposPublicacao("mensagem"),
              ],
            },
          }
        );
        if (aula) {
          if (
            !aula.idCurso ||
            !aula.idSerie ||
            !aula.idTurma ||
            !aula.idMateria
          ) {
            throw new Error(
              "Erro ao recuperar os IDs do curso, série, turma ou matéria"
            );
          }

          if (!aula.mensagem) {
            const categoria = mensagemCategorias.find(
              (f) => f.tipo == EMensagemCategoriaTipo["Tarefas de Casa"]
            );
            if (categoria && categoria.id) {
              setMensagemCategoria(categoria);
              aula.mensagem = getMensagemDefault({
                aula,
                usuario,
                idMensagemCategoria: categoria.id,
                mensagemCategoria: categoria,
              });
            }
          }
          if (aula.turma && !aula.turma.chamadaFixada) {
            api.put(`turma/${aula.turma.id}`, {
              ...aula.turma,
              chamadaFixada: true,
            });
            gravaLogUser({
              logKey: {
                ...Log.Alunos.Alterar,
                acao: EAcaoLogUser.Alteracao,
                grupoAcao: EGrupoAcaoLogUser.Aula,
              },
              logKeys: {
                idProfessor: Number(aula.idProfessor),
                idMateria: Number(aula.idMateria),
                idCurso: Number(aula.idCurso),
                idSerie: Number(aula.idSerie),
                idTurma: Number(aula.idTurma),
              },
              status: ELogUserStatus.Aguardando,
              message: `alterou chamada fixada ${aula.aula} id: ${aula.id}`,
              dataBefore: { ...aula.turma },
              dataAfter: { ...aula.turma },
            });
          }

          if (aula.naGrade) {
            const { data: horarios } = await api.get<
              IHorarioAulaDiaAula[] | undefined
            >(
              `diaaula/aulas-geminadas/${aula.idTurma}/materia/${aula.idMateria}/data/${aula.data}`,
              {
                cache: { ignoreCache: true },
              }
            );

            if (Array.isArray(horarios) && horarios.length > 0) {
              setHorarios([...horarios]);
            }
          }

          const {
            data: [observacoes],
          } = await api.get<[ITurmaObs[]]>("observacao-turma", {
            cache: { ignoreCache: true },
            params: {
              where: {
                anoLetivo: aula.anoLetivo,
                idTurma: aula.idTurma,
                data: aula.data,
                idMateria: aula.idMateria,
              },
            },
          });

          if (observacoes.length > 0) setTurmaObs(observacoes.at(0));
          else {
            setTurmaObs({
              ...yupTurmaObs.getDefault(),
              anoLetivo: aula.anoLetivo,
              data: aula.data,
              idCurso: aula.idCurso,
              idSerie: aula.idSerie,
              idTurma: aula.idTurma,
              idMateria: aula.idMateria,
              idProfessor:
                usuario?.tipoUsuario == EUserType.Professor
                  ? usuario.id || null
                  : null,
              observacao: "",
            });
          }

          setAula({ ...aula });
          setTextoTarefaInicial(aula.mensagem?.texto || null);
          setTextoAulaInicial(aula.descricao);
          setTextoObsInicial(turmaObs?.observacao || null);

          setCarregaAlunos(true);
        }
      } catch (error) {
        snack.error(getError(error));
      } finally {
        setLoading(false);
      }
    })();
  }, [idDiaAula]);

  useEffect(() => {
    return () => {
      if (precisaNotificarTarefa) notificaTarefa();
    };
  }, [precisaNotificarTarefa]);

  useEffect(() => {
    if (modificou) {
      window.addEventListener("beforeunload", alertUser);
    }
    return () => {
      window.removeEventListener("beforeunload", alertUser);
    };
  }, [modificou]);

  useEffect(() => {
    return () => {
      (async () => {
        if (
          aula?.mensagem &&
          textoTarefaInicial != aula.mensagem.texto &&
          !loading
        ) {
          setTextoTarefaInicial(aula.mensagem.texto);
          await gravarTarefa(aula.mensagem);

          setShowTooltipTarefa(true);
          window.setTimeout(() => setShowTooltipTarefa(false), 1000);
        }
        if (aula && textoAulaInicial != aula.descricao && !loading) {
          setTextoAulaInicial(aula.descricao);
          await gravarAula(aula);

          setShowTooltipAula(true);
          window.setTimeout(() => setShowTooltipAula(false), 1000);
        }
        if (turmaObs && textoObsInicial != turmaObs.observacao && !loading) {
          setTextoObsInicial(turmaObs.observacao);
          await gravarObservacao(turmaObs);

          setShowTooltipObs(true);
          window.setTimeout(() => setShowTooltipObs(false), 1000);
        }
      })();
    };
  }, [
    loading,
    textoAulaInicial,
    aula,
    textoTarefaInicial,
    turmaObs,
    textoObsInicial,
  ]);

  useEffect(() => {
    if (aula && carregaAlunos) {
      getAlunos({
        emRecuperacao: aula.isRecuperacao,
        etapaRecuperacao: aula.isRecuperacao ? recEtapa?.etapa : undefined,
      });
      setCarregaAlunos(false);
    }
  }, [aula, recEtapa, carregaAlunos]);

  return (
    <Main
      useScroll={false}
      maxWidth={false}
      useMargin={false}
      loading={loading}
      title={aula?.turma?.tituloTurma ?? ""}
      subtitle={
        <Subtitle
          isRecuperacao={isRecuperacao}
          aula={aula}
          dataAula={dataAula}
          recEtapa={recEtapa}
          sistemaAvaliacao={sistemaAvaliacao}
        />
      }
      rightbarChildren={
        <>
          {loading && <LinearProgress />}
          {!loading && aula && (
            <DadosAula
              key={`${aula.id}-${turmaObs?.id}`}
              aula={aula}
              gravarAula={gravarAula}
              gravarTarefa={gravarTarefa}
              setAula={setAula}
              mensagem={aula.mensagem}
              apagarTodasFaltas={apagarTodasFaltas}
              conteudoCopiado={conteudoCopiado}
              setConteudoCopiado={setConteudoCopiado}
              removerTarefa={removerTarefa}
              setMsgErrorAttr={setMsgErrorAttr}
              msgErrorAttr={msgErrorAttr}
              setTextoAulaInicial={setTextoAulaInicial}
              setTextoTarefaInicial={setTextoTarefaInicial}
              showTooltipAula={showTooltipAula}
              showTooltipTarefa={showTooltipTarefa}
              setModificou={setModificou}
              modificou={modificou}
              turmaObs={turmaObs}
              setTextoObsInicial={setTextoObsInicial}
              showTooltipObs={showTooltipObs}
              removerObservacao={removerObservacao}
              isRecuperacao={isRecuperacao}
              setCarregaAlunos={setCarregaAlunos}
            />
          )}
        </>
      }
      rightbarIcon={<SettingIcon />}
    >
      <DialogAlunosRecuperacao>
        {recEtapa && aula && (
          <AlunosRecuperacao
            idDiaAula={idDiaAula}
            recEtapa={recEtapa}
            onClose={(reload) => {
              if (reload) {
                getAlunos({
                  emRecuperacao: aula.isRecuperacao,
                  etapaRecuperacao: recEtapa?.etapa,
                });
              }
              hideDialogAlunosRecuperacao();
            }}
          />
        )}
      </DialogAlunosRecuperacao>
      <Backdrop open={gerandoAula} message="Gerando dia de aula" />
      {dataAula && aula && alunos && (
        <Alunos
          key={aula.id}
          alunos={alunos}
          aula={aula}
          horarios={horarios}
          apagarFalta={apagarFalta}
          gerarFalta={gerarFalta}
          handleChangeAula={handleChangeAula}
          handleAlunosRecuperacao={showDialogAlunosRecuperacao}
          loading={loading}
        />
      )}
    </Main>
  );
};
